import React, { HTMLAttributeAnchorTarget } from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

export interface ButtonProps extends React.AllHTMLAttributes<HTMLDivElement> {
  link?: string;
  align?: 'left' | 'center' | 'right';
  left?: React.ReactNode;
  appearance?: 'default' | 'primary' | 'warning' | 'danger';
  customColor?: string;
  btnSize?: 'small' | 'medium';
  handleClick?: Function;
  textColor?: string;
  borderColor?: string;
  className?: string;
  target?: HTMLAttributeAnchorTarget;
}

const StyledButtonContentText = styled.div`
  font-weight: 500;
`;
export const StyledButtonContentLeft = styled.div`
  display: flex;
`;
const StyledButtonContentRightAlign = css`
  justify-content: flex-end;
`;
const StyledButtonContentCenterAlign = css`
  justify-content: center;
`;
const StyledButtonContentLeftAlign = css`
  justify-content: flex-start;
`;
const StyledButtonContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;
const StyledButtonWarning = css`
  color: white;
  background: ${({ theme }) => theme.colors.ORANGE.O400};

  :hover {
    background: ${({ theme }) => theme.colors.ORANGE.O500};
  }
`;
const StyledButtonDanger = css`
  color: white;
  background: ${({ theme }) => theme.colors.RED.R400};

  :hover {
    background: ${({ theme }) => theme.colors.RED.R500};
  }
`;
const StyledButtonPrimary = css`
  color: white;
  background: ${({ theme }) => theme.colors.BLUE.B400};

  :hover {
    background: ${({ theme }) => theme.colors.BLUE.B500};
  }
`;
export const StyledButtonLink = styled.div<{
  disabled: boolean;
}>`
  ${props => props.disabled && `pointer-events: none;`};

  & > a {
    display: flex;
    ${props => props.disabled && `pointer-events: none;`};
  }
`;
const StyledButtonSizeMedium = css`
  gap: 20px;
  font-size: 14px;
  padding: 10px 15px;
`;
const StyledButtonSizeSmall = css`
  gap: 10px;
  font-size: 12px;
  padding: 5px 10px;
`;
const StyledButtonHover = css`
  :hover {
    background: ${({ theme }) => theme.colors.BORDER};
  }
`;
export const StyledButton = styled.div<{
  appearance: string;
  disabled: boolean;
  align: string;
  btnSize: string;
  customColor?: string;
  textColor?: string;
  borderColor?: string;
}>`
  display: flex;
  align-items: center;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
  background: ${({ theme }) => theme.colors.BG};

  ${props => props.btnSize === 'small' && StyledButtonSizeSmall};
  ${props => props.btnSize === 'medium' && StyledButtonSizeMedium};
  ${props => props.disabled && `opacity: 0.4;pointer-events: none;`};
  ${props => props.align === 'left' && StyledButtonContentLeftAlign};
  ${props => props.align === 'center' && StyledButtonContentCenterAlign};
  ${props => props.align === 'right' && StyledButtonContentRightAlign};

  &,
  & * {
    user-select: none;
  }

  ${props => !props.customColor && StyledButtonHover};

  ${props =>
    props.customColor && `color: white; background: ${props.customColor};`};
  ${props => props.textColor && `color: ${props.textColor};`};
  ${props => props.borderColor && `border: 2px solid ${props.borderColor};`};
  ${props => props.appearance === 'primary' && StyledButtonPrimary};
  ${props => props.appearance === 'warning' && StyledButtonWarning};
  ${props => props.appearance === 'danger' && StyledButtonDanger};
`;

const ButtonComponent = ({
  appearance = 'default',
  customColor,
  left,
  align = 'center',
  disabled = false,
  link,
  handleClick: _handleClick,
  children,
  btnSize = 'medium',
  textColor,
  borderColor,
  className,
  target = '_blank',
  ...restProps
}: ButtonProps) => {
  const handleClick = () => {
    _handleClick && _handleClick();
  };

  const renderContent = (
    <StyledButton
      align={align}
      appearance={appearance}
      customColor={customColor}
      disabled={disabled}
      btnSize={btnSize}
      textColor={textColor}
      borderColor={borderColor}
      onClick={handleClick}
      style={restProps.style}
      className={className}
    >
      {left && <StyledButtonContentLeft>{left}</StyledButtonContentLeft>}
      <StyledButtonContent>
        <StyledButtonContentText>{children}</StyledButtonContentText>
      </StyledButtonContent>
    </StyledButton>
  );

  return link ? (
    <StyledButtonLink disabled={disabled}>
      <Link to={link} target={target}>
        {renderContent}
      </Link>
    </StyledButtonLink>
  ) : (
    renderContent
  );
};

export default ButtonComponent;
